import { useEffect } from 'react';

/**
 * SEO — per-page title, meta description, canonical URL, and optional JSON-LD schema.
 * Addresses site audit finding D5.1 (per-page meta differentiation) and D5.5 (schema).
 *
 * Uses direct document API instead of react-helmet-async to avoid React 19 compatibility
 * issues with <title>{title}</title> JSX children.
 */
const BASE_URL = 'https://www.zazued.com';
const DEFAULT_OG_IMAGE = 'https://images.unsplash.com/photo-1599666527768-e8cf85741436?w=1200&h=630&fit=crop';

const setMeta = (attr, key, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const existed = !!el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return { el, prev, existed };
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  const existed = !!el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, existed };
};

const setSchema = (schema, id) => {
  if (!schema) return null;
  const scriptId = `seo-schema-${id}`;
  let el = document.getElementById(scriptId);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.id = scriptId;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
  return el;
};

const SEO = ({ title, description, path = '', schema, ogImage }) => {
  useEffect(() => {
    const canonical = `${BASE_URL}${path}`;
    const image = ogImage || DEFAULT_OG_IMAGE;

    const prevTitle = document.title;
    if (title) document.title = title;

    const metaRefs = [
      setMeta('name', 'description', description),
      setMeta('property', 'og:title', title),
      setMeta('property', 'og:description', description),
      setMeta('property', 'og:url', canonical),
      setMeta('property', 'og:image', image),
      setMeta('property', 'og:type', 'website'),
      setMeta('name', 'twitter:card', 'summary_large_image'),
      setMeta('name', 'twitter:title', title),
      setMeta('name', 'twitter:description', description),
      setMeta('name', 'twitter:image', image),
    ].filter(Boolean);

    const canonicalRef = setCanonical(canonical);

    const schemaId = path.replace(/[^a-z0-9]/gi, '-') || 'home';
    const schemaEl = setSchema(schema, schemaId);

    return () => {
      document.title = prevTitle;
      metaRefs.forEach(({ el, prev, existed }) => {
        if (existed && prev !== null) el.setAttribute('content', prev);
      });
      if (canonicalRef.existed && canonicalRef.prev !== null) {
        canonicalRef.el.setAttribute('href', canonicalRef.prev);
      }
      if (schemaEl) schemaEl.remove();
    };
  }, [title, description, path, schema, ogImage]);

  return null;
};

export default SEO;
